import { Component, EventEmitter, Input, OnChanges, OnInit, Output } from '@angular/core';
import { differenceBy } from 'lodash-es';
import { Subject, combineLatest } from 'rxjs';
import { catchError, takeUntil } from 'rxjs/operators';

import { DashpivotEvent, EventNotifierService, EventTypes } from '@site-mate/dashpivot-shared-library';

import { TemplateSelection } from 'app/apps/template-selection.model';
import { TemplatesService } from 'app/apps/templates.service';
import { IBusinessAttribute } from 'app/business-attributes/business-attribute.model';
import { BusinessAttributesService } from 'app/business-attributes/business-attributes.service';
import { SegmentService } from 'app/segment/segment.service';
import { Tab } from 'app/shared/model/template-tab.model';
import { TemplateWeb } from 'app/shared/model/template.model';
import { ErrorHandler } from 'app/shared/service/error-handler.service';
import { RouteParamsService } from 'app/shared/service/route-params.service';

import { TemplateSelectorViewTypes } from './template-selector-view-types.enum';

@Component({
  selector: 'cc-templates-selector',
  templateUrl: 'templates-selector.component.html',
  styleUrls: ['templates-selector.component.scss'],
})
export class TemplatesSelectorComponent implements OnInit, OnChanges {
  teamId: string;
  keyword: string;
  visibleTemplates: TemplateWeb[] = [];
  templateSelectorViewTypes = TemplateSelectorViewTypes;
  tabs: Tab[] = [];
  selectedTabIds: string[] = [];
  maxBadgesToDisplay = 30;
  displayRoleSelectorNeeded = false;
  verticals: IBusinessAttribute[] = [];
  roles: IBusinessAttribute[] = [];
  functions: IBusinessAttribute[] = [];
  selectedVerticals: IBusinessAttribute[] = [];
  selectedRoles: IBusinessAttribute[] = [];
  selectedFunctions: IBusinessAttribute[] = [];
  showClearFiltersBtn: boolean;
  isBusinessAttributesLoading = true;
  clearFilters = new EventEmitter();
  onSelectionChange = new EventEmitter<void>();

  private destroy$ = new Subject<void>();

  @Input() isSingleSelectionOnly = false;
  @Input() loading = true;
  @Input() templates: TemplateWeb[] = [];
  @Input() selectedTemplates: TemplateWeb[] = [];
  @Input() companyTemplateTabs: Tab[] = [];
  @Input() isLibrarySelection: boolean;
  @Input() isDefaultTextColor = true;
  @Input() viewType: TemplateSelectorViewTypes = TemplateSelectorViewTypes.Grid;
  @Input() selectedBusinessAttributes: string[];

  @Output() onSelect = new EventEmitter();
  @Output() isAnyTemplateSelected = new EventEmitter();
  @Output() facetsChange = new EventEmitter<IBusinessAttribute[]>();

  constructor(
    private readonly segmentService: SegmentService,
    private readonly templatesService: TemplatesService,
    private readonly businessAttributesService: BusinessAttributesService,
    private readonly errorHandler: ErrorHandler,
    private readonly routeParamsService: RouteParamsService,
  ) {}

  ngOnInit(): void {
    const errorHandler = (error) => {
      this.errorHandler.handle(error);
      return [];
    };

    const verticals$ = this.businessAttributesService
      .getVerticals()
      .pipe(takeUntil(this.destroy$), catchError(errorHandler));

    const roles$ = this.businessAttributesService
      .getRoles()
      .pipe(takeUntil(this.destroy$), catchError(errorHandler));

    const functions$ = this.businessAttributesService
      .getFunctions()
      .pipe(takeUntil(this.destroy$), catchError(errorHandler));

    combineLatest([verticals$, roles$, functions$, this.routeParamsService.get('company')]).subscribe(
      ([verticals, roles, functions, company]) => {
        this.verticals = verticals ?? [];
        this.roles = roles ?? [];
        this.functions = functions ?? [];
        this.isBusinessAttributesLoading = false;

        this.setDefaultFilters(company?.businessAttributes);
        this.updateFilters();
      },
    );
  }

  ngOnChanges(): void {
    if (this.loading) {
      return;
    }
    this.tabs = this.templatesService.getSortedTabsFilteredByTemplates(
      this.companyTemplateTabs,
      this.templates,
    );

    this.filterTemplates();
  }

  onClearFilters() {
    this.clearFilters.emit();
  }

  onVerticalSelection(verticals: IBusinessAttribute[]) {
    this.selectedVerticals = verticals;
    this.updateFilters();
  }

  onRoleSelection(roles: IBusinessAttribute[]) {
    this.selectedRoles = roles;
    this.updateFilters();
  }

  onFunctionSelection(functions: IBusinessAttribute[]) {
    this.selectedFunctions = functions;
    this.updateFilters();
  }

  onSearchKeyWord(keyword: string) {
    this.keyword = keyword;
    void EventNotifierService.notify(
      new DashpivotEvent(EventTypes.TemplateFilterApplied, { Context: 'Search by Text', value: keyword }),
      this.segmentService,
    );
    this.filterTemplates();
  }

  onTemplateSelection(templateSelection?: TemplateSelection) {
    const { templateId, isSelected } = templateSelection ?? {};
    let affectedTemplates = [];

    if (this.isSingleSelectionOnly) {
      this.selectedTemplates = [];
    }

    if (templateId === 'All') {
      affectedTemplates = this.visibleTemplates;
    } else {
      affectedTemplates = this.templates.filter(({ _id }) => _id === templateId);
    }

    this.selectedTemplates = differenceBy(this.selectedTemplates, affectedTemplates, `_id`);

    if (isSelected) {
      this.selectedTemplates.push(...affectedTemplates);
    }

    this.updateSelection();
  }

  clearSelection() {
    this.selectedTemplates = [];
    this.updateSelection();
    this.onSelectionChange.emit();
  }

  deselect(template: TemplateWeb) {
    this.onTemplateSelection({ templateId: template._id, isSelected: false });
    this.onSelectionChange.emit();
  }

  changeViewType(type: TemplateSelectorViewTypes) {
    this.viewType = type;
  }

  onFilterByTabs(tabId: string) {
    this.selectedTabIds = tabId?.length ? [tabId] : [];
    this.filterTemplates();
  }

  private setDefaultFilters(companyBusinessAttributes: string[]) {
    const defaultBusinessAttributes = this.selectedBusinessAttributes ?? companyBusinessAttributes;
    this.selectedVerticals = this.verticals.filter((vert) =>
      defaultBusinessAttributes?.some((dba) => dba === vert._id),
    );

    this.selectedRoles = this.roles.filter((role) =>
      defaultBusinessAttributes?.some((dba) => dba === role._id),
    );
  }

  private updateFilters() {
    const filters = [...this.selectedVerticals, ...this.selectedFunctions, ...this.selectedRoles];
    this.showClearFiltersBtn = !!filters.length;
    this.facetsChange.emit(filters);
  }

  private filterTemplates() {
    this.visibleTemplates = this.templatesService.filterTemplatesByKeywordAndTag(
      this.templates,
      this.keyword,
      this.selectedTabIds,
    );

    this.updateSelection();
  }

  private updateSelection() {
    this.templates.forEach((template: TemplateWeb) => {
      template.selected = this.selectedTemplates.some(({ _id }) => _id === template._id);
    });
    this.isAnyTemplateSelected.emit(this.selectedTemplates.length > 0);
  }
}
